import React, { useEffect, useState } from "react";
import { Globe2 } from "lucide-react";
import { api, getActiveLocation, setActiveLocation } from "../lib/api";

/**
 * Global-admin-only persistent location selector.
 *
 * - Renders nothing unless the signed-in user is a global admin.
 * - Stores the chosen location in localStorage; the api axios interceptor
 *   auto-injects it as `?location=` on every GET. POST/PATCH/DELETE are
 *   never touched (the backend silently stamps the user's own location on
 *   creates and enforces matching on writes).
 * - "All locations" clears the override → admin sees every record.
 * - Switching reloads the page so every open table/dashboard re-fetches
 *   under the new scope in one consistent step.
 */
export default function LocationSwitcher({ isGlobalAdmin, currentUserLocation }) {
  const [options, setOptions] = useState([]);
  const [value, setValue] = useState(getActiveLocation() || "");

  useEffect(() => {
    if (!isGlobalAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/locations");
        if (cancelled) return;
        setOptions((data?.locations || []).filter(Boolean));
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isGlobalAdmin]);

  if (!isGlobalAdmin) return null;

  const onChange = (e) => {
    const next = e.target.value;
    setValue(next);
    setActiveLocation(next);
    // Hard reload so every open list / dashboard re-fetches under the new
    // scope without us having to wire a global "refresh" event into every page.
    window.location.reload();
  };

  return (
    <div
      className="flex items-center gap-1.5 px-2 py-1 border-2 border-rose-500/40 bg-rose-500/10 rounded-sm"
      data-testid="header-location-switcher-wrapper"
      title="Acting-as location (global admin)"
    >
      <Globe2 className="h-3.5 w-3.5 text-amber-400" strokeWidth={2.5} />
      <select
        value={value}
        onChange={onChange}
        data-testid="header-location-switcher"
        className="bg-transparent text-white text-xs font-bold uppercase tracking-wider focus:outline-none cursor-pointer pr-1"
      >
        <option value="" className="text-slate-900">
          All locations
        </option>
        {options.map((loc) => (
          <option key={loc} value={loc} className="text-slate-900">
            {loc}
            {loc === currentUserLocation ? " (home)" : ""}
          </option>
        ))}
      </select>
    </div>
  );
}
